import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building2,
  ExternalLink,
  FileUp,
  Lock,
  Pencil,
  Search,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch } from '@/lib/api';

interface Client {
  id: string;
  codigoCliente?: string | null;
  cpfCnpj: string;
  razaoSocial: string;
  nomeFantasia?: string | null;
  inscricaoMunicipal?: string | null;
  email?: string | null;
  telefone?: string | null;
  enderecoLogradouro?: string | null;
  enderecoNumero?: string | null;
  enderecoComplemento?: string | null;
  enderecoBairro?: string | null;
  enderecoCidade: string;
  enderecoUf: string;
  enderecoCep?: string | null;
}

interface ListResponse {
  rows: Client[];
  total: number;
}

interface BrasilApiResult {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
  email?: string;
  telefone?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;
}

interface ImportResponse {
  imported: number;
  updated: number;
  skipped: number;
  issues: Array<{ line: number; field: string; message: string }>;
}

type SortField = 'codigoCliente' | 'razaoSocial' | 'cpfCnpj' | 'cidadeUf' | 'email';
type SortDir = 'asc' | 'desc';

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

function sortKey(c: Client, field: SortField): string {
  switch (field) {
    case 'codigoCliente':
      return c.codigoCliente ?? '';
    case 'razaoSocial':
      return c.razaoSocial ?? '';
    case 'cpfCnpj':
      return c.cpfCnpj ?? '';
    case 'cidadeUf':
      return `${c.enderecoCidade}/${c.enderecoUf}`;
    case 'email':
      return c.email ?? '';
    default:
      return '';
  }
}

function formatCpfCnpj(s: string): string {
  const d = digitsOnly(s);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return d;
}

const SORT_LABELS: Record<SortField, string> = {
  codigoCliente: 'Código',
  razaoSocial: 'Razão social',
  cpfCnpj: 'CPF/CNPJ',
  cidadeUf: 'Cidade/UF',
  email: 'E-mail',
};

export function ClientsPage() {
  const { user } = useAuth();
  const canDelete = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [sortField, setSortField] = useState<SortField>('razaoSocial');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['clients', search],
    queryFn: () =>
      apiFetch<ListResponse>(`/clients?pageSize=200${search ? `&search=${encodeURIComponent(search)}` : ''}`),
  });

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = [...data.rows];
    const factor = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const va = sortKey(a, sortField).toLowerCase();
      const vb = sortKey(b, sortField).toLowerCase();
      // ordena vazios sempre por último
      if (!va && vb) return 1;
      if (va && !vb) return -1;
      if (!va && !vb) return 0;
      return va.localeCompare(vb, 'pt-BR', { numeric: true }) * factor;
    });
    return rows;
  }, [data, sortField, sortDir]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/clients/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clients'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Clientes</h1>
          <p className="text-sm text-muted-foreground">
            Tomadores de serviço. Use a busca para localizar por razão social, código ou CPF/CNPJ.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent"
          >
            <FileUp className="h-4 w-4" /> Importar CSV
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <UserPlus className="h-4 w-4" /> Novo cliente
          </button>
        </div>
      </header>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          placeholder="Buscar por razão social, código ou CPF/CNPJ…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {isError && <p className="p-4 text-sm text-destructive">Erro ao carregar clientes.</p>}
        {data && data.rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <Building2 className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {search ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado ainda.'}
            </p>
          </div>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                {(Object.keys(SORT_LABELS) as SortField[]).map((field) => {
                  const active = sortField === field;
                  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                  return (
                    <th key={field} className="px-4 py-2 font-medium">
                      <button
                        type="button"
                        onClick={() => handleSort(field)}
                        className={`flex items-center gap-1 hover:text-foreground ${
                          active ? 'text-foreground' : 'text-muted-foreground'
                        }`}
                        title={`Ordenar por ${SORT_LABELS[field]}`}
                      >
                        {SORT_LABELS[field]}
                        <Icon className="h-3 w-3" />
                      </button>
                    </th>
                  );
                })}
                {canDelete && <th className="px-2 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                  <td className="px-4 py-2 font-mono text-xs">{c.codigoCliente ?? '—'}</td>
                  <td className="px-4 py-2 font-medium">
                    {c.razaoSocial}
                    {c.nomeFantasia && (
                      <div className="text-xs text-muted-foreground">{c.nomeFantasia}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{formatCpfCnpj(c.cpfCnpj)}</td>
                  <td className="px-4 py-2">
                    {c.enderecoCidade}/{c.enderecoUf}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{c.email ?? '—'}</td>
                  {canDelete && (
                    <td className="px-2 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/clients/${c.id}`}
                          title="Abrir detalhes (contatos, cofre…)"
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                        <button
                          type="button"
                          data-dica="Editar"
                          onClick={() => setEditing(c)}
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/clients/${c.id}?tab=cofre`}
                          title="Cofre — senhas, tokens, acessos"
                          className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          <Lock className="h-4 w-4" />
                        </Link>
                        <button
                          type="button"
                          data-dica="Excluir"
                          onClick={() => {
                            if (confirm(`Excluir cliente ${c.razaoSocial}?`)) {
                              deleteMutation.mutate(c.id);
                            }
                          }}
                          className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {data && (
        <p className="text-xs text-muted-foreground">
          {data.total} cliente(s) {search && 'encontrados'}
        </p>
      )}

      {showCreate && <ClientFormDialog onClose={() => setShowCreate(false)} />}
      {editing && <ClientFormDialog initial={editing} onClose={() => setEditing(null)} />}
      {showImport && <ImportCsvDialog onClose={() => setShowImport(false)} />}
    </div>
  );
}

function ClientFormDialog({
  initial,
  onClose,
}: {
  initial?: Client;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const [form, setForm] = useState({
    codigoCliente: initial?.codigoCliente ?? '',
    cpfCnpj: initial?.cpfCnpj ?? '',
    razaoSocial: initial?.razaoSocial ?? '',
    nomeFantasia: initial?.nomeFantasia ?? '',
    email: initial?.email ?? '',
    telefone: initial?.telefone ?? '',
    enderecoLogradouro: initial?.enderecoLogradouro ?? '',
    enderecoNumero: initial?.enderecoNumero ?? '',
    enderecoBairro: initial?.enderecoBairro ?? '',
    enderecoCidade: initial?.enderecoCidade ?? 'Jacareí',
    enderecoUf: initial?.enderecoUf ?? 'SP',
    enderecoCep: initial?.enderecoCep ?? '',
  });
  const [lookupErr, setLookupErr] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (cnpj: string) =>
      apiFetch<BrasilApiResult>(`/clients/lookup-cnpj/${digitsOnly(cnpj)}`),
    onSuccess: (r) => {
      setForm((s) => ({
        ...s,
        razaoSocial: r.razaoSocial || s.razaoSocial,
        nomeFantasia: r.nomeFantasia ?? s.nomeFantasia,
        email: r.email ?? s.email,
        telefone: r.telefone ?? s.telefone,
        enderecoLogradouro: r.logradouro ?? s.enderecoLogradouro,
        enderecoNumero: r.numero ?? s.enderecoNumero,
        enderecoBairro: r.bairro ?? s.enderecoBairro,
        enderecoCidade: r.municipio ?? s.enderecoCidade,
        enderecoUf: r.uf ?? s.enderecoUf,
        enderecoCep: r.cep ?? s.enderecoCep,
      }));
      setLookupErr(null);
    },
    onError: () => setLookupErr('CNPJ não encontrado na BrasilAPI.'),
  });

  function buildPayload(): Record<string, string | undefined> {
    return {
      codigoCliente: form.codigoCliente || undefined,
      cpfCnpj: digitsOnly(form.cpfCnpj),
      razaoSocial: form.razaoSocial,
      nomeFantasia: form.nomeFantasia || undefined,
      email: form.email || undefined,
      telefone: form.telefone ? digitsOnly(form.telefone) : undefined,
      enderecoLogradouro: form.enderecoLogradouro || undefined,
      enderecoNumero: form.enderecoNumero || undefined,
      enderecoBairro: form.enderecoBairro || undefined,
      enderecoCidade: form.enderecoCidade || undefined,
      enderecoUf: form.enderecoUf || undefined,
      enderecoCep: form.enderecoCep ? digitsOnly(form.enderecoCep) : undefined,
    };
  }

  const save = useMutation({
    mutationFn: () =>
      apiFetch<Client>(isEdit ? `/clients/${initial!.id}` : '/clients', {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(buildPayload()),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitErr('CPF/CNPJ já cadastrado.');
      } else {
        setSubmitErr(
          err instanceof Error ? err.message : isEdit ? 'Erro ao salvar cliente.' : 'Erro ao criar cliente.',
        );
      }
    },
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setSubmitErr(null);
    save.mutate();
  }

  return (
    <Dialog title={isEdit ? `Editar cliente — ${initial!.razaoSocial}` : 'Novo cliente'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="space-y-1 md:col-span-2">
            <span className="text-xs font-medium">CNPJ ou CPF</span>
            <div className="flex gap-2">
              <input
                required
                inputMode="numeric"
                disabled={isEdit}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono disabled:opacity-60"
                value={isEdit ? formatCpfCnpj(form.cpfCnpj) : form.cpfCnpj}
                onChange={(e) => setForm((s) => ({ ...s, cpfCnpj: e.target.value }))}
              />
              {!isEdit && (
                <button
                  type="button"
                  disabled={lookup.isPending || digitsOnly(form.cpfCnpj).length !== 14}
                  onClick={() => lookup.mutate(form.cpfCnpj)}
                  className="flex items-center gap-1 rounded-md border border-input bg-background px-3 text-xs whitespace-nowrap hover:bg-accent disabled:opacity-50"
                >
                  <Search className="h-3 w-3" /> Buscar CNPJ
                </button>
              )}
            </div>
            {lookupErr && <span className="text-xs text-destructive">{lookupErr}</span>}
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">Código interno</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.codigoCliente}
              onChange={(e) => setForm((s) => ({ ...s, codigoCliente: e.target.value }))}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-xs font-medium">Razão social *</span>
            <input
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.razaoSocial}
              onChange={(e) => setForm((s) => ({ ...s, razaoSocial: e.target.value }))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">Nome fantasia</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.nomeFantasia}
              onChange={(e) => setForm((s) => ({ ...s, nomeFantasia: e.target.value }))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">E-mail</span>
            <input
              type="email"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">Telefone</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.telefone}
              onChange={(e) => setForm((s) => ({ ...s, telefone: e.target.value }))}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="space-y-1 md:col-span-3">
            <span className="text-xs font-medium">Logradouro</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.enderecoLogradouro}
              onChange={(e) => setForm((s) => ({ ...s, enderecoLogradouro: e.target.value }))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">Número</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.enderecoNumero}
              onChange={(e) => setForm((s) => ({ ...s, enderecoNumero: e.target.value }))}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="space-y-1">
            <span className="text-xs font-medium">Bairro</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.enderecoBairro}
              onChange={(e) => setForm((s) => ({ ...s, enderecoBairro: e.target.value }))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">Cidade</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.enderecoCidade}
              onChange={(e) => setForm((s) => ({ ...s, enderecoCidade: e.target.value }))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">UF</span>
            <input
              maxLength={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
              value={form.enderecoUf}
              onChange={(e) =>
                setForm((s) => ({ ...s, enderecoUf: e.target.value.toUpperCase() }))
              }
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium">CEP</span>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              value={form.enderecoCep}
              onChange={(e) => setForm((s) => ({ ...s, enderecoCep: e.target.value }))}
            />
          </label>
        </div>

        {submitErr && (
          <p role="alert" className="text-sm text-destructive">
            {submitErr}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {save.isPending
              ? isEdit
                ? 'Salvando…'
                : 'Criando…'
              : isEdit
                ? 'Salvar alterações'
                : 'Criar cliente'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ImportCsvDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);

  const upload = useMutation({
    mutationFn: (content: string) =>
      apiFetch<ImportResponse>('/clients/import', {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        body: content,
      }),
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  async function handleFile(file: File) {
    const text = await file.text();
    upload.mutate(text);
  }

  return (
    <Dialog title="Importar clientes (CSV)" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Formato: separador <code>;</code>, primeira linha = header. Colunas suportadas:{' '}
          <code>codigo_cliente, cpf_cnpj, razao_social, nome_fantasia, email, telefone,
          logradouro, numero, complemento, bairro, cidade, uf, cep</code>.
        </p>

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
          className="block text-sm"
        />

        {upload.isPending && <p className="text-sm text-muted-foreground">Importando…</p>}
        {upload.isError && (
          <p className="text-sm text-destructive">
            Falha no upload: {upload.error instanceof Error ? upload.error.message : 'erro'}
          </p>
        )}

        {result && (
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3 text-sm">
            <p>
              <strong>{result.imported}</strong> criados, <strong>{result.updated}</strong>{' '}
              atualizados, <strong>{result.skipped}</strong> ignorados.
            </p>
            {result.issues.length > 0 && (
              <details className="text-xs">
                <summary>{result.issues.length} issue(s)</summary>
                <ul className="mt-2 list-disc pl-4">
                  {result.issues.slice(0, 20).map((i, idx) => (
                    <li key={idx}>
                      {i.line ? `Linha ${i.line}: ` : ''}
                      {i.field} — {i.message}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
